<template>
  <div class="flex min-h-screen items-center justify-center bg-background p-4">
    <div class="w-full max-w-md rounded-xl border bg-card p-8 text-card-foreground shadow-xl">
      <div class="mb-8 flex items-center justify-between">
        <h1 class="text-3xl font-bold">Регистрация по приглашению</h1>
        <button
          @click="toggleTheme"
          class="button button-outline !h-9 !w-9 !px-0"
          title="Переключить тему"
        >
          <svg v-if="!isDark" class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />
          </svg>
          <svg v-else class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
          </svg>
        </button>
      </div>
      
      <div v-if="error" class="mb-6 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {{ error }}
      </div>

      <form @submit.prevent="register" class="space-y-5">
        <div>
          <label for="username" class="mb-2 block text-sm font-medium">Username:</label>
          <input
            type="text"
            id="username"
            name="username"
            v-model="formData.username"
            required
            class="input-field"
          />
        </div>

        <div>
          <label for="title" class="mb-2 block text-sm font-medium">Имя:</label>
          <input
            type="text"
            id="title"
            name="title"
            v-model="formData.title"
            required
            class="input-field"
          />
        </div>

        <div>
          <label for="description" class="mb-2 block text-sm font-medium">Позиция/Должность (опционально):</label>
          <input
            type="text"
            id="description"
            name="description"
            v-model="formData.description"
            class="input-field"
          />
        </div>

        <div>
          <label for="password" class="mb-2 block text-sm font-medium">Пароль:</label>
          <input
            type="password"
            id="password"
            name="password"
            v-model="formData.password"
            required
            class="input-field"
          />
        </div>

        <div>
          <label for="confirm_password" class="mb-2 block text-sm font-medium">Повторите пароль:</label>
          <input
            type="password"
            id="confirm_password"
            name="confirm_password"
            v-model="formData.confirm_password"
            required
            class="input-field"
          />
        </div>

        <!-- Поле для загрузки аватарки -->
        <FileUploadField
          label="Фото профиля"
          :current-file="null"
          accept="image/*"
          accept-text="Изображения (JPG, PNG, GIF)"
          :max-size="5 * 1024 * 1024"
          :custom-upload-with-progress="customPhotoUpload"
          @file-uploaded="handlePhotoUploaded"
          @file-removed="onPhotoRemoved"
          @file-cancelled="onPhotoCancelled"
        />

        <button
          type="submit"
          class="button w-full"
          :disabled="loading"
        >
          <span v-if="loading">Регистрация...</span>
          <span v-else>Зарегистрироваться</span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import FileUploadField from './FileUploadField.vue'
import { useTheme } from '../utils/useTheme'

export default {
  name: 'RegisterByInvitation',
  components: {
    FileUploadField
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    
    const { currentTheme, toggleTheme: toggleThemeFunc } = useTheme()
    const isDark = computed(() => currentTheme.value === 'dark')
    
    const API_URL = (process.env.VUE_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '')
    
    const formData = reactive({
      username: '',
      title: '',
      description: '',
      password: '',
      confirm_password: '',
    })
    
    const selectedPhotoId = ref(null)
    
    const loading = ref(false)
    const error = ref('')
    const token = ref(null)
    
    onMounted(() => {
      token.value = route.query.token
      if (!token.value) {
        error.value = 'Отсутствует токен приглашения'
      }
    })
    
    const validateForm = () => {
      if (formData.password !== formData.confirm_password) {
        error.value = 'Пароли не совпадают'
        return false
      }
      
      if (formData.password.length < 8) {
        error.value = 'Пароль должен быть не менее 8 символов'
        return false
      }
      
      return true
    }
    
    const register = async () => {
      if (!validateForm()) {
        return
      }
      
      loading.value = true
      error.value = ''
      
      try {
        const response = await fetch(`${API_URL}/users/register-by-invitation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            token: token.value,
            photo_id: selectedPhotoId.value
          })
        })
        
        const data = await response.json()
        
        if (!response.ok) {
          throw new Error(data.detail || 'Ошибка регистрации')
        }
        
        // Авторизуем пользователя после регистрации
        const loginResponse = await fetch(`${API_URL}/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams({
            username: formData.username,
            password: formData.password
          }).toString()
        })
        
        const loginData = await loginResponse.json()
        
        if (!loginResponse.ok) {
          throw new Error('Ошибка авторизации')
        }
        
        // Сохраняем токены
        localStorage.setItem('token', loginData.access_token)
        localStorage.setItem('refresh_token', loginData.refresh_token)
        
        // Переходим в админку
        router.push('/')
        
      } catch (err) {
        console.error('Ошибка регистрации:', err)
        error.value = err.message || 'Ошибка при регистрации'
      } finally {
        loading.value = false
      }
    }
    
    const customPhotoUpload = (file, onProgress = () => {}, onError = () => {}) => {
      return new Promise((resolve, reject) => {
        if (!token.value) {
          const err = new Error('Токен приглашения отсутствует')
          error.value = err.message
          onError(err)
          reject(err)
          return
        }
        
        const formData = new FormData()
        formData.append('file', file)
        
        const url = new URL(`${API_URL}/users/register-by-invitation/upload-photo`)
        url.searchParams.append('invitation_token', token.value)
        
        const xhr = new XMLHttpRequest()
        
        xhr.open('POST', url.toString(), true)
        
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100)
            onProgress(progress)
          }
        })
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText)
              if (response.id) {
                selectedPhotoId.value = response.id
                error.value = ''
                resolve(response)
              } else {
                const err = new Error(response.detail || 'Ошибка загрузки файла')
                error.value = err.message
                onError(err)
                reject(err)
              }
            } catch (e) {
              const err = new Error('Ошибка парсинга ответа сервера')
              error.value = err.message
              onError(err)
              reject(err)
            }
          } else {
            const err = new Error(`HTTP Error: ${xhr.status}`)
            error.value = err.message
            onError(err)
            reject(err)
          }
        })
        
        xhr.addEventListener('error', () => {
          const err = new Error('Ошибка сети при загрузке файла')
          error.value = err.message
          onError(err)
          reject(err)
        })
        
        xhr.addEventListener('abort', () => {
          const err = new Error('Загрузка файла отменена')
          error.value = err.message
          onError(err)
          reject(err)
        })
        
        xhr.send(formData)
      })
    }
    
    const handlePhotoUploaded = (response) => {
      console.log('Фото загружено:', response)
    }
    
    const onPhotoRemoved = () => {
      selectedPhotoId.value = null
    }
    
    const onPhotoCancelled = () => {
      // Ничего не делаем при отмене
    }
    
    return {
      formData,
      selectedPhotoId,
      loading,
      error,
      register,
      customPhotoUpload,
      handlePhotoUploaded,
      onPhotoRemoved,
      onPhotoCancelled,
      isDark,
      toggleTheme: toggleThemeFunc
    }
  }
}
</script>
